import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogContentText, 
    DialogActions, 
    Button, 
    TextField,
    Typography,
    Box
} from '@mui/material';

function ConfirmDialog({ 
    open, 
    onClose, 
    onConfirm, 
    title = "Confirm Delete",
    message,
    resourceType,
    resourceName,
    requireTypeConfirm = true,
    confirmButtonText = "Delete",
    cancelButtonText = "Cancel"
}) {
    const [typeConfirm, setTypeConfirm] = useState('');
    const [isConfirmDisabled, setIsConfirmDisabled] = useState(true);

    useEffect(() => {
        if (requireTypeConfirm) {
            setIsConfirmDisabled(typeConfirm !== 'DELETE');
        } else {
            setIsConfirmDisabled(false);
        }
    }, [typeConfirm, requireTypeConfirm]);

    // Handle ENTER key shortcut for confirmation
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (open && event.key === 'Enter' && !isConfirmDisabled) {
                event.preventDefault();
                event.stopPropagation();
                handleConfirm();
            }
        };

        if (open) {
            document.addEventListener('keydown', handleKeyPress);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [open, isConfirmDisabled]);

    const handleClose = () => {
        setTypeConfirm('');
        onClose();
    };

    const handleConfirm = () => {
        setTypeConfirm('');
        onConfirm();
    };

    const getDisplayMessage = () => {
        if (message) return message;
        
        const resource = resourceType ? `${resourceType} ` : '';
        const name = resourceName ? `"${resourceName}"` : 'this item';
        return `Are you sure you want to delete ${resource}${name}? This action cannot be undone.`;
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
                {title}
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {getDisplayMessage()}
                </DialogContentText>
                
                {requireTypeConfirm && (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                            To confirm, type <strong>DELETE</strong> in the box below:
                        </Typography>
                        <TextField
                            autoFocus
                            margin="dense"
                            fullWidth
                            variant="outlined"
                            placeholder="Type DELETE to confirm"
                            value={typeConfirm}
                            onChange={(e) => setTypeConfirm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isConfirmDisabled) {
                                    e.preventDefault();
                                    handleConfirm();
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'error.main',
                                    },
                                }
                            }}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={handleClose} 
                    color="primary"
                >
                    {cancelButtonText}
                </Button>
                <Button 
                    onClick={handleConfirm} 
                    color="error"
                    variant="contained"
                    disabled={isConfirmDisabled}
                >
                    {confirmButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ConfirmDialog;
